import { t } from "@lingui/macro";
import {
  CircleNotch,
  FadersHorizontal,
  ReadCvLogo,
  SketchLogo,
  SignOut,
} from "@phosphor-icons/react";
import { Button, KeyboardShortcut, Separator } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { Link, useLocation, useNavigate } from "react-router";
import useKeyboardShortcut from "use-keyboard-shortcut";

import { Logo } from "@/client/components/logo";
import { UserAvatar } from "@/client/components/user-avatar";
import { useUser } from "@/client/services/user";
import { useBillingPortal } from "@/client/services/billing";
import { useToast } from "@/client/hooks/use-toast";
import { useLogout } from "@/client/services/auth";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { LocaleSwitch } from "@/client/components/locale-switch";
import { useLingui } from "@lingui/react";

type Props = {
  className?: string;
};

type SidebarItem = {
  path: string;
  name: string;
  shortcut?: string;
  icon: React.ReactNode;
};

type SidebarItemProps = SidebarItem & {
  onClick?: () => void;
};

const SidebarItem = ({ path, name, shortcut, icon, onClick }: SidebarItemProps) => {
  const isActive = useLocation().pathname === path;

  return (
    <Button
      asChild
      size="lg"
      variant="ghost"
      className={cn(
        "h-auto justify-start rounded-lg px-4 py-3",
        isActive && "bg-secondary text-secondary-foreground pointer-events-none",
      )}
      onClick={onClick}
    >
      <Link to={path}>
        <div className="mr-3">{icon}</div>
        <span>{name}</span>
        {!isActive && <KeyboardShortcut className="ml-auto">{shortcut}</KeyboardShortcut>}
      </Link>
    </Button>
  );
};

type SidebarProps = {
  setOpen?: (open: boolean) => void;
};

export const Sidebar = ({ setOpen }: SidebarProps) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { getBillingPortal, isLoading } = useBillingPortal();
  const { toast } = useToast();
  const { logout } = useLogout();

  useKeyboardShortcut(["shift", "r"], () => {
    void navigate("/dashboard/resumes");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "s"], () => {
    void navigate("/dashboard/settings");
    setOpen?.(false);
  });

  const sidebarItems: SidebarItem[] = [
    {
      path: "/dashboard/resumes",
      name: t`Resumes`,
      shortcut: "⇧R",
      icon: <ReadCvLogo />,
    },
    {
      path: "/dashboard/settings",
      name: t`Settings`,
      shortcut: "⇧S",
      icon: <FadersHorizontal />,
    },
  ];

  const handleManageBilling = async () => {
    try {
      const { url } = await getBillingPortal();

      toast({
        title: t`Redirecting to billing portal`,
        description: t`You will be redirected to the Stripe billing portal in a moment.`,
      });

      // Short delay to show the toast before redirecting
      setTimeout(() => {
        window.location.href = url;
      }, 1500);
    } catch (error) {
      console.error("Failed to get billing portal URL:", error);

      toast({
        variant: "error",
        title: t`Error`,
        description: t`Failed to get billing portal URL. Please try again.`,
      });
    }
  };

  return (
    <div className="flex h-full flex-col gap-y-4">
      <div className="flex flex-col items-center">
        <Link className="flex size-full flex-col items-center text-2xl font-bold" to="/dashboard">
          <Logo size={52} />
          cvkit
        </Link>
      </div>

      <div className="flex w-full items-center justify-around">
        <ThemeSwitch />
        <LocaleSwitch />
      </div>

      <Separator className="opacity-50" />

      <div className="grid gap-y-2">
        {sidebarItems.map((item) => (
          <SidebarItem {...item} key={item.path} onClick={() => setOpen?.(false)} />
        ))}
      </div>

      <div className="flex-1" />

      <Separator className="opacity-50" />

      {user?.plusUser && !user?.isFreeTrial ? (
        <Button variant="outline" onClick={handleManageBilling} disabled={isLoading}>
          {isLoading ? (
            <>
              <CircleNotch className="mr-2 size-4 animate-spin" />
              {t`Loading...`}
            </>
          ) : (
            t`Manage Subscription`
          )}
        </Button>
      ) : (
        <Button
          variant="outline"
          className="group h-12 gap-3 rounded-lg border-yellow-500 hover:bg-yellow-500"
          onClick={() => {
            void navigate("/dashboard/upgrade");
          }}
        >
          <SketchLogo
            className="group-hover:text-secondary-foreground text-yellow-500"
            size={24}
            weight="duotone"
          />
          {t`Upgrade to Plus`}
        </Button>
      )}

      <div className="flex items-center gap-x-3">
        <UserAvatar size={36} className="select-none" />
        {user?.isAnonymous ? (
          <Button
            variant="link"
            onClick={() => {
              void navigate("/auth/register");
            }}
          >
            {t`Create Account or Login`}
          </Button>
        ) : (
          <div className="flex w-full justify-between">
            <span className="text-base font-semibold">{user?.name}</span>
            <Button variant="link" className="gap-2" onClick={() => logout()}>
              <SignOut weight="bold" size={16} /> {t`Logout`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
